import React, { useCallback, useEffect, useState } from 'react';
import CopyButton from '../../shared/components/CopyButton';
import Modal from '../../shared/components/Modal';
import { extractErrorMessage } from '../../shared/utils/errors';
import { DepositSigner } from '../../shared/types';
import { listPoolSigners, revokePoolSigner } from './api';

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

interface DepositSignersSectionProps {
  canRevoke: boolean;
  onRevoked?: () => void;
}

/**
 * Trusted deposit signing keys. The list itself is compiled into the
 * accounting service; this screen only shows usage and lets an operator
 * revoke a key, which deactivates every address it signed at once.
 */
const DepositSignersSection: React.FC<DepositSignersSectionProps> = ({ canRevoke, onRevoked }) => {
  const [signers, setSigners] = useState<DepositSigner[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<DepositSigner | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeConfirm, setRevokeConfirm] = useState('');
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [revokeSuccess, setRevokeSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setSigners(await listPoolSigners());
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load signing keys'));
      setSigners(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openRevoke = (signer: DepositSigner) => {
    setRevokeTarget(signer);
    setRevokeReason('');
    setRevokeConfirm('');
    setRevokeError(null);
    setRevokeSuccess(null);
  };

  const closeRevoke = () => {
    setRevokeTarget(null);
    setRevokeError(null);
    setRevokeSuccess(null);
  };

  const confirmRevoke = async () => {
    if (!revokeTarget || revokeConfirm !== 'REVOKE') return;
    setRevokeSubmitting(true);
    setRevokeError(null);
    try {
      const result = await revokePoolSigner(revokeTarget.kid, revokeReason.trim() || undefined);
      setRevokeSuccess(
        result.already_revoked
          ? 'Key was already revoked.'
          : `Key revoked: ${result.wallets_deactivated} wallet${result.wallets_deactivated === 1 ? '' : 's'} deactivated across ${result.batches} batch${result.batches === 1 ? '' : 'es'}.`
      );
      load();
      onRevoked?.();
    } catch (err) {
      setRevokeError(extractErrorMessage(err, 'Failed to revoke key'));
    } finally {
      setRevokeSubmitting(false);
    }
  };

  return (
    <>
      <div className="card p-4 text-sm text-slate-400">
        Trusted keys are compiled into the accounting service and cannot be added here — adding one
        is a code change that both owners must approve. Revoking a key takes effect immediately and
        deactivates every deposit address it signed, including addresses already assigned to users.
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-red-400">{error}</p>
          </div>
        ) : !signers?.length ? (
          <div className="p-12 text-center text-slate-400">
            No trusted signing keys are configured. Signed batches cannot be imported until a key is
            added in code.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="table-header">Key</th>
                  <th className="table-header">Status</th>
                  <th className="table-header text-right">Batches</th>
                  <th className="table-header text-right">Addresses</th>
                  <th className="table-header text-right">Wallets</th>
                  <th className="table-header">Revocation</th>
                  <th className="table-header text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {signers.map((signer) => (
                  <tr key={signer.kid} className="hover:bg-slate-800/30 transition-colors">
                    <td className="table-cell text-xs max-w-[320px]">
                      <p className="text-slate-200 font-medium">{signer.label || '—'}</p>
                      <p className="font-mono text-slate-400 break-all">{signer.kid}</p>
                      {signer.jwk && (
                        <details className="mt-1 text-slate-500">
                          <summary className="cursor-pointer hover:text-slate-300">JWK</summary>
                          <pre className="font-mono text-[10px] whitespace-pre-wrap break-all mt-1">
                            {JSON.stringify(signer.jwk, null, 2)}
                          </pre>
                        </details>
                      )}
                    </td>
                    <td className="table-cell whitespace-nowrap">
                      {signer.revoked ? (
                        <span className="badge badge-danger text-[10px]">Revoked</span>
                      ) : !signer.in_code ? (
                        <span className="badge badge-warning text-[10px]" title="No longer in the compiled-in list; its batches fail verification">
                          Removed from code
                        </span>
                      ) : (
                        <span className="badge badge-success text-[10px]">Trusted</span>
                      )}
                    </td>
                    <td className="table-cell text-right font-mono text-slate-200">{signer.batches}</td>
                    <td className="table-cell text-right font-mono text-slate-200">{signer.addresses}</td>
                    <td
                      className="table-cell text-right font-mono text-slate-300 whitespace-nowrap"
                      title={`${signer.active_wallets} active of ${signer.wallets}`}
                    >
                      {signer.active_wallets}/{signer.wallets}
                    </td>
                    <td className="table-cell text-xs text-slate-400 max-w-[240px]">
                      {signer.revoked ? (
                        <>
                          <p>{formatDateTime(signer.revoked_at)}</p>
                          <p className="text-slate-500">{signer.revoked_by || '—'}</p>
                          {signer.revoke_reason && (
                            <p className="text-slate-500 truncate" title={signer.revoke_reason}>
                              {signer.revoke_reason}
                            </p>
                          )}
                        </>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="table-cell text-right">
                      <div className="inline-flex gap-1 justify-end">
                        <CopyButton value={signer.kid} label="Copy kid" small />
                        {canRevoke && !signer.revoked && (
                          <button
                            type="button"
                            onClick={() => openRevoke(signer)}
                            className="btn btn-danger py-1 px-3 text-xs"
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={revokeTarget !== null} onClose={closeRevoke} title="Revoke signing key" size="md">
        {revokeTarget && (
          <div className="space-y-4">
            <p className="text-sm text-slate-300">
              This immediately deactivates{' '}
              <span className="font-mono text-white">{revokeTarget.active_wallets}</span> active
              deposit address{revokeTarget.active_wallets === 1 ? '' : 'es'} signed by this key,
              including addresses already assigned to users. Deposits arriving on them will no
              longer be credited. Revocation cannot be undone from the backoffice.
            </p>
            <div className="card p-3 bg-slate-800/40 text-xs space-y-1">
              <p className="text-slate-200">{revokeTarget.label || '—'}</p>
              <p className="font-mono text-slate-400 break-all">{revokeTarget.kid}</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Reason (optional)</label>
              <input
                type="text"
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                className="input py-2"
                maxLength={500}
                placeholder="e.g. key compromised"
                disabled={revokeSubmitting || revokeSuccess !== null}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">
                Type <span className="font-mono text-slate-200">REVOKE</span> to confirm
              </label>
              <input
                type="text"
                value={revokeConfirm}
                onChange={(e) => setRevokeConfirm(e.target.value)}
                className="input py-2 font-mono"
                autoComplete="off"
                spellCheck={false}
                disabled={revokeSubmitting || revokeSuccess !== null}
              />
            </div>

            {revokeError && <p className="text-sm text-red-400">{revokeError}</p>}
            {revokeSuccess && <p className="text-sm text-emerald-400">{revokeSuccess}</p>}

            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={closeRevoke} className="btn btn-secondary">
                {revokeSuccess ? 'Close' : 'Cancel'}
              </button>
              {!revokeSuccess && (
                <button
                  type="button"
                  onClick={confirmRevoke}
                  disabled={revokeSubmitting || revokeConfirm !== 'REVOKE'}
                  className="btn btn-danger disabled:opacity-60"
                >
                  {revokeSubmitting ? 'Revoking…' : 'Revoke key'}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default DepositSignersSection;
